import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressBar } from 'primereact/progressbar';
import api from '../../services/api';
import { BORDER_OPTIONS, BORDER_STATES, BORDER_CITIES_BY_STATE } from '../../constants/constants';
import CityGeoMapPicker from './CityGeoMapPicker';
import { hasInstructions, instructionsStepCount } from './CityGeoInstructionsDialog';

const TRANSPORT_MODES = ['passenger', 'commercial', 'pedestrian'];

const TRANSPORT_META = {
  passenger: { icon: 'pi pi-car', color: '#475569' },
  commercial: { icon: 'pi pi-truck', color: '#475569' },
  pedestrian: { color: '#475569' },
};

function WalkingPersonIcon({ color = '#0d9488', size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9 7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7z" />
    </svg>
  );
}

function TransportModeIcon({ mode, color, size = 16 }) {
  if (mode === 'pedestrian') return <WalkingPersonIcon color={color} size={size} />;
  const meta = TRANSPORT_META[mode];
  return <i className={meta?.icon || 'pi pi-map-marker'} style={{ color, fontSize: size }} />;
}

function groupSlotKey(crossingName, portNumber, transportMode) {
  return `${(crossingName || '').trim()}::${(portNumber || '').trim()}::${(transportMode || '').trim()}`;
}

function crossingMarkerKey(crossingName) {
  return (crossingName || '').trim();
}

function makeSlotKey(crossingName, portNumber, transportMode, laneTypes) {
  const base = groupSlotKey(crossingName, portNumber, transportMode);
  if (!laneTypes?.length) return base;
  const suffix = [...laneTypes].map((lane) => String(lane).trim()).filter(Boolean).sort().join('+');
  return suffix ? `${base}::${suffix}` : base;
}

function buildSlotsFromEntryPage(page) {
  const crossings = page?.crossing_names_section?.crossings_names || [];
  const slots = [];
  for (const crossing of crossings) {
    const crossingName = (crossing?.name || '').trim();
    if (!crossingName) continue;
    for (const port of crossing.ports || []) {
      const portNumber = String(port?.port_number || '').trim();
      if (!portNumber) continue;
      const visibility = port.visibility || {};
      for (const transportMode of TRANSPORT_MODES) {
        const lanes = Array.isArray(visibility[transportMode])
          ? visibility[transportMode].filter(Boolean)
          : [];
        if (!lanes.length) continue;
        slots.push({
          key: makeSlotKey(crossingName, portNumber, transportMode),
          crossingName,
          portNumber,
          portName: port.port_name || '',
          transportMode,
          laneTypes: lanes,
        });
      }
    }
  }
  return slots;
}

function coordsFromPlace(map) {
  const coords =
    map?.destinationPoint?.coordinates || map?.garitaLocation?.coordinates;
  if (coords?.length >= 2) return { lng: coords[0], lat: coords[1] };
  if (map?.lat != null && map?.lng != null) return { lat: map.lat, lng: map.lng };
  return { lat: null, lng: null };
}

function queueGeofenceOf(map) {
  return map?.queueGeofence || map?.geometry || null;
}

function crossingRadiusOf(map) {
  return map?.crossingRadiusMeters ?? map?.arrivalRadiusMeters ?? 150;
}

function crossingZoneModeOf(map) {
  const raw = String(map?.crossingZoneMode || '').toLowerCase();
  if (raw === 'geofence' || raw === 'polygon') return 'geofence';
  if (raw === 'radius') return 'radius';
  return map?.crossingGeofence ? 'geofence' : 'radius';
}

function pointFromLatLng(lat, lng) {
  if (lat == null || lng == null) return null;
  return { type: 'Point', coordinates: [Number(lng), Number(lat)] };
}

function hasQueuePath(map) {
  const coords = map?.queuePath?.coordinates;
  return Array.isArray(coords) && coords.length >= 2;
}

function isMapComplete(map) {
  const poly = queueGeofenceOf(map)?.coordinates?.[0];
  const pin = coordsFromPlace(map);
  return (
    Array.isArray(poly) &&
    poly.length >= 4 &&
    pin.lat != null &&
    pin.lng != null &&
    hasQueuePath(map)
  );
}

/** One overview marker per crossing name (not per port / route). */
function mergeCrossings(maps = [], saved = []) {
  const byKey = new Map();
  for (const item of saved || []) {
    const name = (item.crossingName || '').trim();
    if (!name) continue;
    const key = crossingMarkerKey(item.key || name);
    const pin = coordsFromPlace(item);
    byKey.set(key, {
      key,
      crossingName: name,
      portNumber: String(item.portNumber || '').trim(),
      portName: item.portName || '',
      destinationPoint: item.destinationPoint || item.garitaLocation || pointFromLatLng(pin.lat, pin.lng),
      lat: pin.lat,
      lng: pin.lng,
      crossingRadiusMeters: crossingRadiusOf(item),
      placeId: item.placeId,
    });
  }
  for (const map of maps || []) {
    const name = (map.crossingName || '').trim();
    if (!name) continue;
    const key = crossingMarkerKey(name);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        crossingName: name,
        portNumber: String(map.portNumber || '').trim(),
        portName: map.portName || '',
        destinationPoint: null,
        lat: null,
        lng: null,
        crossingRadiusMeters: 150,
      });
      continue;
    }
    if (!existing.portName && map.portName) existing.portName = map.portName;
    if (!existing.portNumber && map.portNumber) {
      existing.portNumber = String(map.portNumber).trim();
    }
  }
  return [...byKey.values()];
}

function pageLabel(page) {
  if (!page) return '';
  const title = page.h2 || page.title || page.full_slug || page.slug || page._id;
  return page.lang ? `${title} (${page.lang})` : title;
}

function idOf(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value._id || null;
}

function mergeMaps(fromPage, saved = []) {
  const savedItems = saved || [];
  const used = new Set();
  const result = [];

  for (const slot of fromPage) {
    const matching = [];
    savedItems.forEach((item, idx) => {
      if (used.has(idx)) return;
      if (
        (item.crossingName || '').trim() === slot.crossingName &&
        String(item.portNumber || '').trim() === slot.portNumber &&
        (item.transportMode || '').trim() === slot.transportMode
      ) {
        matching.push({ item, idx });
      }
    });

    if (!matching.length) {
      result.push({
        ...slot,
        clientId: slot.key,
        availableLaneTypes: slot.laneTypes,
        queueGeofence: null,
        geometry: null,
        queuePath: null,
        destinationPoint: null,
        lat: null,
        lng: null,
        crossingRadiusMeters: 150,
        crossingZoneMode: 'radius',
        crossingGeofence: null,
        instructions: null,
      });
      continue;
    }

    const available = slot.laneTypes || [];
    const assigned = new Set();
    const kept = [];
    for (const { item, idx } of matching) {
      used.add(idx);
      let lanes = (item.laneTypes?.length ? item.laneTypes : available).filter(
        (lane) => available.includes(lane) && !assigned.has(lane)
      );
      if (!lanes.length && matching.length === 1) {
        lanes = available.filter((lane) => !assigned.has(lane));
      }
      if (!lanes.length) continue;
      lanes.forEach((lane) => assigned.add(lane));
      kept.push({ item, lanes });
    }

    const multi = kept.length > 1;
    for (const { item, lanes } of kept) {
      const pin = coordsFromPlace(item);
      result.push({
        ...slot,
        ...item,
        key: makeSlotKey(slot.crossingName, slot.portNumber, slot.transportMode, multi ? lanes : undefined),
        clientId: item.clientId || item.key || makeSlotKey(slot.crossingName, slot.portNumber, slot.transportMode, lanes),
        laneTypes: lanes,
        availableLaneTypes: available,
        queueGeofence: queueGeofenceOf(item),
        geometry: queueGeofenceOf(item),
        queuePath: item.queuePath || null,
        destinationPoint: item.destinationPoint || item.garitaLocation || null,
        garitaLocation: item.destinationPoint || item.garitaLocation || null,
        lat: pin.lat,
        lng: pin.lng,
        crossingRadiusMeters: crossingRadiusOf(item),
        crossingZoneMode: crossingZoneModeOf(item),
        crossingGeofence: item.crossingGeofence || null,
        instructions: hasInstructions(item.instructions)
          ? item.instructions
          : null,
        geofenceId: item.geofenceId,
        placeId: item.placeId,
      });
    }
  }

  return result;
}

export default function CityGeoEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cityGeo, setCityGeo] = useState(null);
  const [entryPages, setEntryPages] = useState([]);
  const [baseEntryPageId, setBaseEntryPageId] = useState(null);
  const [complementIds, setComplementIds] = useState([]);
  const [maps, setMaps] = useState([]);
  const [crossings, setCrossings] = useState([]);
  const [active, setActive] = useState(true);
  const [openKeys, setOpenKeys] = useState(() => new Set());

  const cityName = useMemo(() => {
    if (!cityGeo) return '';
    return (BORDER_CITIES_BY_STATE[cityGeo.state] || []).find((c) => c.code === cityGeo.city)?.name || cityGeo.city;
  }, [cityGeo]);

  const stateName = useMemo(() => {
    if (!cityGeo) return '';
    return BORDER_STATES.find((s) => s.code === cityGeo.state)?.name || cityGeo.state;
  }, [cityGeo]);

  const borderName = useMemo(() => {
    if (!cityGeo) return '';
    return BORDER_OPTIONS.find((b) => b.code === cityGeo.border)?.name || cityGeo.border;
  }, [cityGeo]);

  const pageOptions = useMemo(
    () =>
      (entryPages || []).map((page) => ({
        label: pageLabel(page),
        value: page._id,
        translations: page.translations || [],
        page,
      })),
    [entryPages]
  );

  const complementOptions = useMemo(
    () => pageOptions.filter((opt) => opt.value !== baseEntryPageId),
    [pageOptions, baseEntryPageId]
  );

  const completeCount = maps.filter(isMapComplete).length;
  const overviewCount = crossings.filter((c) => c.lat != null && c.lng != null).length;
  const [savePortalHost, setSavePortalHost] = useState(null);

  useEffect(() => {
    fetchCityGeo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Google Maps sometimes leaves tall nodes that grow document scroll past the admin shell.
  // Portal Save into admin-main (non-scrolling) so it stays pinned while content scrolls.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const adminMain = document.querySelector('.admin-main');
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
      rootHeight: root?.style.height || '',
      rootOverflow: root?.style.overflow || '',
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.height = '100%';
    if (root) {
      root.style.height = '100%';
      root.style.overflow = 'hidden';
    }
    adminMain?.classList.add('city-geo-admin-main');
    setSavePortalHost(adminMain);
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      html.style.height = prev.htmlHeight;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.height = prev.rootHeight;
        root.style.overflow = prev.rootOverflow;
      }
      adminMain?.classList.remove('city-geo-admin-main');
      setSavePortalHost(null);
    };
  }, []);

  const applyHydration = (pages, baseId, savedMaps, savedCrossings) => {
    const basePage = pages.find((p) => p._id === baseId);
    const nextMaps = basePage
      ? mergeMaps(buildSlotsFromEntryPage(basePage), savedMaps)
      : savedMaps || [];
    setMaps(nextMaps);
    setCrossings(mergeCrossings(nextMaps, savedCrossings || []));
  };

  const fetchCityGeo = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/city_geos/${id}`);
      setCityGeo(data);
      setEntryPages(data.entryPages || []);
      const baseId = idOf(data.baseEntryPageId);
      setBaseEntryPageId(baseId);
      setComplementIds((data.complementEntryPageIds || []).map(idOf).filter(Boolean));
      setActive(data.active !== false);
      applyHydration(data.entryPages || [], baseId, data.maps || [], data.crossings || []);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load city geo' });
    } finally {
      setLoading(false);
    }
  };

  const handleBaseChange = (nextId) => {
    setBaseEntryPageId(nextId);
    setComplementIds((prev) => prev.filter((cid) => cid !== nextId));
    const page = entryPages.find((p) => p._id === nextId);
    if (page?.translations?.length) {
      const translationIds = page.translations
        .map((t) => idOf(t.entry_page_id))
        .filter((tid) => tid && tid !== nextId);
      if (translationIds.length) setComplementIds(translationIds);
    }
    applyHydration(entryPages, nextId, maps, crossings);
  };

  const updateCrossing = (key, patch) => {
    setCrossings((prev) =>
      prev.map((item) => {
        if ((item.key || crossingMarkerKey(item.crossingName)) !== key) return item;
        const next = { ...item };
        if ('lat' in patch) next.lat = patch.lat;
        if ('lng' in patch) next.lng = patch.lng;
        if ('crossingRadiusMeters' in patch || 'arrivalRadiusMeters' in patch) {
          next.crossingRadiusMeters =
            patch.crossingRadiusMeters ?? patch.arrivalRadiusMeters;
        }
        if (patch.lat != null && patch.lng != null) {
          next.destinationPoint = pointFromLatLng(patch.lat, patch.lng);
        } else if ('lat' in patch && patch.lat == null && 'lng' in patch && patch.lng == null) {
          next.destinationPoint = null;
        }
        return next;
      })
    );
  };

  const updateMap = (key, patch) => {
    setMaps((prev) =>
      prev.map((item) => {
        if (item.key !== key && item.clientId !== key) return item;
        const next = { ...item };
        if ('queueGeofence' in patch || 'geometry' in patch) {
          next.queueGeofence = patch.queueGeofence ?? patch.geometry;
          next.geometry = next.queueGeofence;
        }
        if ('queuePath' in patch) next.queuePath = patch.queuePath;
        if ('lat' in patch) next.lat = patch.lat;
        if ('lng' in patch) next.lng = patch.lng;
        if ('crossingRadiusMeters' in patch || 'arrivalRadiusMeters' in patch) {
          next.crossingRadiusMeters =
            patch.crossingRadiusMeters ?? patch.arrivalRadiusMeters;
        }
        if ('crossingZoneMode' in patch) next.crossingZoneMode = patch.crossingZoneMode;
        if ('crossingGeofence' in patch) next.crossingGeofence = patch.crossingGeofence;
        if ('instructions' in patch) {
          next.instructions = patch.instructions;
        }
        if (patch.lat != null && patch.lng != null) {
          next.destinationPoint = pointFromLatLng(patch.lat, patch.lng);
        } else if ('lat' in patch && patch.lat == null && 'lng' in patch && patch.lng == null) {
          next.destinationPoint = null;
        }
        return next;
      })
    );
  };

  const remainingLanes = (slot) => {
    const available = slot.availableLaneTypes || slot.laneTypes || [];
    const siblings = maps.filter(
      (item) =>
        item.crossingName === slot.crossingName &&
        String(item.portNumber) === String(slot.portNumber) &&
        item.transportMode === slot.transportMode
    );
    const assigned = new Set(siblings.flatMap((item) => item.laneTypes || []));
    return available.filter((lane) => !assigned.has(lane));
  };

  const groupCount = (slot) =>
    maps.filter(
      (item) =>
        item.crossingName === slot.crossingName &&
        String(item.portNumber) === String(slot.portNumber) &&
        item.transportMode === slot.transportMode
    ).length;

  const refreshGroupKeys = (items) => {
    const multi = items.length > 1;
    return items.map((item) => ({
      ...item,
      key: makeSlotKey(
        item.crossingName,
        item.portNumber,
        item.transportMode,
        multi ? item.laneTypes : undefined
      ),
    }));
  };

  const replaceGroup = (list, inGroup, nextGroup) => {
    const result = [];
    let inserted = false;
    for (const item of list) {
      if (!inGroup(item)) {
        result.push(item);
        continue;
      }
      if (!inserted) {
        result.push(...nextGroup);
        inserted = true;
      }
    }
    if (!inserted) result.push(...nextGroup);
    return result;
  };

  const updateLaneTypes = (key, nextLanes) => {
    const selected = (nextLanes || []).filter(Boolean);
    if (!selected.length) return;
    setMaps((prev) => {
      const current = prev.find((item) => item.key === key || item.clientId === key);
      if (!current) return prev;
      const available = current.availableLaneTypes || current.laneTypes || [];
      const chosen = selected.filter((lane) => available.includes(lane));
      if (!chosen.length) return prev;

      const inGroup = (item) =>
        item.crossingName === current.crossingName &&
        String(item.portNumber) === String(current.portNumber) &&
        item.transportMode === current.transportMode;

      const grouped = prev
        .filter(inGroup)
        .map((item) => {
          if (item.key === key || item.clientId === key) return { ...item, laneTypes: chosen };
          const leftover = (item.laneTypes || []).filter((lane) => !chosen.includes(lane));
          return leftover.length ? { ...item, laneTypes: leftover } : null;
        })
        .filter(Boolean);

      return replaceGroup(prev, inGroup, refreshGroupKeys(grouped));
    });
  };

  const addMapForRemaining = (slot) => {
    const leftover = remainingLanes(slot);
    if (!leftover.length) return;
    const createdKey = makeSlotKey(slot.crossingName, slot.portNumber, slot.transportMode, leftover);
    setMaps((prev) => {
      const inGroup = (item) =>
        item.crossingName === slot.crossingName &&
        String(item.portNumber) === String(slot.portNumber) &&
        item.transportMode === slot.transportMode;
      const created = {
        key: createdKey,
        clientId: createdKey,
        crossingName: slot.crossingName,
        portNumber: slot.portNumber,
        portName: slot.portName,
        transportMode: slot.transportMode,
        laneTypes: leftover,
        availableLaneTypes: slot.availableLaneTypes || leftover,
        queueGeofence: null,
        geometry: null,
        queuePath: null,
        destinationPoint: null,
        lat: null,
        lng: null,
        crossingRadiusMeters: 150,
        crossingZoneMode: 'radius',
        crossingGeofence: null,
        instructions: null,
      };
      const nextGroup = refreshGroupKeys([...prev.filter(inGroup), created]);
      return replaceGroup(prev, inGroup, nextGroup);
    });
    setOpenKeys((keys) => {
      const next = new Set(keys);
      next.add(createdKey);
      return next;
    });
  };

  const removeSplitMap = (slot) => {
    if (groupCount(slot) <= 1) return;
    setMaps((prev) => {
      const inGroup = (item) =>
        item.crossingName === slot.crossingName &&
        String(item.portNumber) === String(slot.portNumber) &&
        item.transportMode === slot.transportMode;
      return replaceGroup(
        prev,
        inGroup,
        refreshGroupKeys(prev.filter((item) => inGroup(item) && item.key !== slot.key))
      );
    });
  };

  const toggleOpen = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!baseEntryPageId && maps.some((m) => queueGeofenceOf(m) || m.lat != null)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Select a base page',
        detail: 'Maps are saved on the city, but geofences sync after you pick a base entry page.',
      });
    }
    try {
      setSaving(true);
      const payload = {
        baseEntryPageId,
        complementEntryPageIds: complementIds,
        active,
        crossings: crossings.map((item) => {
          const name = (item.crossingName || '').trim();
          const row = {
            key: crossingMarkerKey(item.key || name),
            crossingName: name,
            portNumber: item.portNumber || '',
            portName: item.portName,
            crossingRadiusMeters: crossingRadiusOf(item),
            placeId: item.placeId,
          };
          if (item.destinationPoint || item.garitaLocation) {
            row.destinationPoint = item.destinationPoint || item.garitaLocation;
          } else if (item.lat != null && item.lng != null) {
            row.lat = item.lat;
            row.lng = item.lng;
          } else {
            row.destinationPoint = null;
            row.lat = null;
            row.lng = null;
          }
          return row;
        }),
        maps: maps.map((item) => {
          const row = {
            key: item.key,
            crossingName: item.crossingName,
            portNumber: item.portNumber,
            portName: item.portName,
            transportMode: item.transportMode,
            laneTypes: item.laneTypes,
            crossingRadiusMeters: crossingRadiusOf(item),
            crossingZoneMode: crossingZoneModeOf(item),
            geofenceId: item.geofenceId,
            placeId: item.placeId,
          };
          row.queueGeofence = queueGeofenceOf(item) || null;
          row.queuePath = item.queuePath || null;
          row.crossingGeofence = item.crossingGeofence || null;
          if (item.destinationPoint || item.garitaLocation) {
            row.destinationPoint = item.destinationPoint || item.garitaLocation;
          } else if (item.lat != null && item.lng != null) {
            row.lat = item.lat;
            row.lng = item.lng;
          } else {
            row.destinationPoint = null;
            row.lat = null;
            row.lng = null;
          }
          if (hasInstructions(item.instructions)) {
            row.instructions = item.instructions;
          } else if (item.instructions === null) {
            row.instructions = null;
          }
          return row;
        }),
      };
      const { data } = await api.put(`/city_geos/${id}`, payload);
      setCityGeo(data);
      setEntryPages(data.entryPages || []);
      applyHydration(
        data.entryPages || [],
        idOf(data.baseEntryPageId),
        data.maps || [],
        data.crossings || []
      );
      const savedComplete = (data.maps || []).filter(isMapComplete).length;
      const hadComplete = maps.filter(isMapComplete).length;
      if (hadComplete > 0 && savedComplete < hadComplete) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Save incomplete',
          detail: `Only ${savedComplete}/${hadComplete} complete routes persisted. Re-draw queue path on the missing slots and Save again.`,
          life: 8000,
        });
      } else {
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'City geo saved' });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || err?.response?.data?.error || 'Failed to save city geo',
      });
    } finally {
      setSaving(false);
    }
  };

  const groupedCrossings = useMemo(() => {
    return crossings.map((crossing) => {
      const name = (crossing.crossingName || '').trim();
      const destKey = crossing.key || crossingMarkerKey(name);
      const routes = maps.filter(
        (m) => (m.crossingName || '').trim() === name
      );
      return { crossing, destKey, routes };
    });
  }, [crossings, maps]);

  if (loading) {
    return (
      <Card>
        <div className="card p-4">
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading city geo...
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="city-geo-editor-card">
      <div className="card p-4 city-geo-editor-body">
        {typeof document !== 'undefined' &&
          createPortal(
            <Toast ref={toast} position="top-right" className="city-geo-toast" />,
            document.body
          )}
        <div className="grid mb-4">
          <div className="col-12 md:col-7 flex align-items-center gap-2">
            <Button
              icon="pi pi-arrow-left"
              className="p-button-text p-button-rounded"
              onClick={() => navigate('/city_geos')}
              aria-label="Back"
            />
            <div>
              <h2 className="m-0 main-text font-bold">{cityName}</h2>
              <small className="text-600">
                {borderName} · {stateName}
              </small>
            </div>
          </div>
          <div className="col-12 md:col-5 flex justify-content-end align-items-center gap-3">
            <div className="flex align-items-center gap-2">
              <InputSwitch checked={active} onChange={(e) => setActive(e.value)} />
              <span>Active</span>
            </div>
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 md:col-6 p-fluid">
            <label className="font-semibold">Base entry page</label>
            <Dropdown
              value={baseEntryPageId}
              options={pageOptions}
              onChange={(e) => handleBaseChange(e.value)}
              placeholder={pageOptions.length ? 'Select the source page' : 'No entry pages for this city'}
              filter
              showClear
              className="w-full mt-1"
            />
            <small className="text-600">
              Crossing names, ports and lanes come from this page. Choose which lane types share a map; add another map for the same port when they do not.
              {baseEntryPageId ? (
                <>
                  {' '}
                  <a href={`/entry_page/edit/${baseEntryPageId}`}>Open entry page</a>
                </>
              ) : null}
            </small>
          </div>
          <div className="col-12 md:col-6 p-fluid">
            <label className="font-semibold">Complement entry pages</label>
            <MultiSelect
              value={complementIds}
              options={complementOptions}
              onChange={(e) => setComplementIds(e.value || [])}
              placeholder="Translations / same city pages"
              filter
              display="chip"
              className="w-full mt-1"
              disabled={!baseEntryPageId}
            />
            <small className="text-600">
              Same geography is applied to these pages (typically the other language).
            </small>
          </div>
        </div>

        <div className="flex align-items-center justify-content-between mb-3 gap-3 flex-wrap">
          <div>
            <h3 className="m-0">Crossing markers & routes</h3>
            <small className="text-600">
              Overview pin per crossing name (app map markers). Each lane/transport route has its own pin, radius, queue path and geofence — group lanes when they share a line.
              {' '}
              {overviewCount}/{crossings.length} markers · {completeCount}/{maps.length} routes
            </small>
          </div>
          <div style={{ minWidth: 180 }}>
            <ProgressBar
              value={maps.length ? Math.round((completeCount / maps.length) * 100) : 0}
              showValue={false}
              style={{ height: 10 }}
            />
          </div>
        </div>

        {!baseEntryPageId && (
          <p className="text-600">Select a base entry page to generate maps from its crossing names.</p>
        )}

        {baseEntryPageId && !maps.length && (
          <p className="text-600">
            The base page has no crossing names with ports and lane visibility. Configure them on the entry page first.
          </p>
        )}

        {groupedCrossings.map(({ crossing, destKey, routes }) => {
          const pinComplete = crossing.lat != null && crossing.lng != null;
          return (
            <div
              key={destKey}
              className="border-1 surface-border border-round mb-4 overflow-hidden city-geo-crossing-block"
            >
              <div className="p-3 surface-50 border-bottom-1 surface-border city-geo-crossing-map">
                <div className="flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                  <div className="flex align-items-center gap-2 flex-wrap">
                    <h4 className="m-0">{crossing.crossingName || 'Unnamed'}</h4>
                    <Tag value="Map marker" severity="secondary" />
                  </div>
                  <Tag
                    value={pinComplete ? 'Pin set' : 'Needs pin'}
                    severity="secondary"
                    style={
                      pinComplete
                        ? { background: '#E8EEF9', color: '#2020A0', border: '1px solid #C5D0EA' }
                        : { background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }
                    }
                  />
                </div>
                <small className="text-600 block mb-2">
                  General location shown on the app map. Tapping it lists the routes below.
                </small>
                <CityGeoMapPicker
                  lat={crossing.lat}
                  lng={crossing.lng}
                  crossingRadiusMeters={crossingRadiusOf(crossing)}
                  pinMode="edit"
                  showGeofence={false}
                  showQueue={false}
                  showInstructions={false}
                  showCrossingZone={false}
                  onChange={(next) => updateCrossing(destKey, next)}
                />
              </div>

              {routes.map((slot) => {
                const slotId = slot.clientId || slot.key;
                const open = openKeys.has(slotId);
                const complete = isMapComplete(slot);
                const transport =
                  TRANSPORT_META[slot.transportMode] || {
                    icon: 'pi pi-map-marker',
                    color: '#4b5563',
                  };
                const leftover = remainingLanes(slot);
                const laneOptions = (slot.availableLaneTypes || slot.laneTypes || []).map(
                  (lane) => ({
                    label: lane,
                    value: lane,
                  })
                );
                const instrOk = hasInstructions(slot.instructions);
                const instrCount = instructionsStepCount(slot.instructions);
                const routeLabel = [
                  slot.portNumber,
                  slot.transportMode,
                  (slot.laneTypes || []).join(' + '),
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <div key={slotId} className="border-top-1 surface-border">
                    <div
                      className="w-full flex align-items-center justify-content-between p-3 cursor-pointer"
                      onClick={() => toggleOpen(slotId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') toggleOpen(slotId);
                      }}
                    >
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
                        <span className="font-semibold text-sm">{slot.portNumber}</span>
                        <span className="flex align-items-center gap-1 font-semibold">
                          <TransportModeIcon
                            mode={slot.transportMode}
                            color={transport.color}
                          />
                          {slot.transportMode}
                        </span>
                        {(slot.laneTypes || []).map((lane) => (
                          <Chip
                            key={lane}
                            label={lane}
                            className="text-xs city-geo-lane-chip"
                            style={{
                              background: '#F3F4F6',
                              color: '#374151',
                              border: '1px solid #E5E7EB',
                            }}
                          />
                        ))}
                        {instrOk && (
                          <span className="city-geo-route-instr-pill">
                            <i className="pi pi-check-circle" />
                            Instructions
                            {instrCount > 0 ? ` · ${instrCount}` : ''}
                          </span>
                        )}
                      </div>
                      <Tag
                        value={complete ? 'Complete' : 'Needs route'}
                        severity="secondary"
                        style={
                          complete
                            ? { background: '#E8EEF9', color: '#2020A0', border: '1px solid #C5D0EA' }
                            : { background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' }
                        }
                      />
                    </div>
                    {open && (
                      <div className="p-3 pt-0">
                        <div className="flex align-items-end gap-2 mb-3 flex-wrap">
                          <div
                            className="city-geo-lane-field"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label className="font-semibold text-sm">Applies to lane types</label>
                            <MultiSelect
                              value={slot.laneTypes || []}
                              options={laneOptions}
                              onChange={(e) => updateLaneTypes(slotId, e.value)}
                              placeholder="Select lane types"
                              display="chip"
                              className="mt-1 city-geo-lane-multiselect"
                              disabled={
                                laneOptions.length < 2 && (slot.laneTypes || []).length <= 1
                              }
                            />
                          </div>
                          {leftover.length > 0 && (
                            <Button
                              type="button"
                              icon="pi pi-plus"
                              label={`Add route for ${leftover.join(', ')}`}
                              size="small"
                              severity="secondary"
                              outlined
                              onClick={() => addMapForRemaining(slot)}
                            />
                          )}
                          {groupCount(slot) > 1 && (
                            <Button
                              type="button"
                              icon="pi pi-trash"
                              label="Remove route"
                              size="small"
                              severity="danger"
                              className="city-geo-remove-route-btn ml-auto"
                              onClick={() => removeSplitMap(slot)}
                            />
                          )}
                        </div>
                        <CityGeoMapPicker
                          queueGeofence={queueGeofenceOf(slot)}
                          geometry={queueGeofenceOf(slot)}
                          queuePath={slot.queuePath}
                          lat={slot.lat}
                          lng={slot.lng}
                          crossingRadiusMeters={crossingRadiusOf(slot)}
                          crossingZoneMode={crossingZoneModeOf(slot)}
                          crossingGeofence={slot.crossingGeofence}
                          instructions={slot.instructions}
                          instructionsLabel={routeLabel || slot.crossingName || 'route'}
                          pinMode="edit"
                          showGeofence
                          showQueue
                          showInstructions
                          onChange={(next) => updateMap(slotId, next)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

      </div>
      {savePortalHost &&
        createPortal(
          <Button
            label="Save"
            icon="pi pi-check"
            className="p-button-primary primary-button city-geo-save-fab"
            loading={saving}
            onClick={handleSave}
          />,
          savePortalHost
        )}
    </Card>
  );
}
